import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import { deletePRD1010_R01, disablePRD1010_R01, downloadExcel_PRD1010_R01, getPdfBlob_PRD1010_R01, getPRD1010_R01AllCount, getPRD1010_R01List, patchPRD1010_R01, postPRD1010_R01 } from "./Api_PRD1010_R01";
import { defaultPRD1010_R01Filter, PRD1010_R01Filter, usePRD1010_R01Store } from "./Store_PRD1010_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { defaultPRD1010_R01Dto, PRD1010_R01Dto } from "./Dto_PRD1010_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { formatDateMonth, formatDateYear4, validateString } from "src/utils/string/stringUtil";
import { refetchUpdateList_Excelgrid } from "src/utils/axios/reFetch";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, fetchFilterConfig_refetch, InsertRowHandler, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import useUserStore from "src/store/useUserStore";
import { useQzPrint } from "src/hooks/useQzPrint";
import qz from "qz-tray";
import { useCommonCodeValidate } from 'src/utils/commonCode/CommonCodeValidateContext';
import {
	buildCommonCodeRequirements,
	PRD1010_R01_ROW_VALIDATE,
} from 'src/utils/commonCode/commonCodeRequirementConfig';


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<PRD1010_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const PRD1010_R01Content = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		PRD1010_R01Filter, setPRD1010_R01Filter,
		PRD1010_R01PastFilter, setPRD1010_R01PastFilter,
		PRD1010_R01List, setPRD1010_R01List, insertPRD1010_R01List, removePRD1010_R01List,
		deletedPRD1010_R01List, addDeletedPRD1010_R01List, removeDeletedPRD1010_R01List,
		selectedPRD1010_R01List, setSelectedPRD1010_R01List, removeSelectedPRD1010_R01List,
		originalPRD1010_R01List, setOriginalPRD1010_R01List,
		allCount, setAllCount,
		setScrollTop,
		initData,
	} = usePRD1010_R01Store();

	const { compCdList, itemCdMap } = useCommonCodeStore();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { printPdfFromBlob } = useQzPrint();

	const { company, userInfo } = useUserStore();

	const setAuth = useActionbarAuthStore((state) => state.setAuth);

	// 권한 정보 로드
	useFetchActionbarAuth("PRD1010_R01");

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

    const [clickable, setClickable] = useState(true);

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedPRD1010_R01List.length === 0 && selectedPRD1010_R01List.length === 0,
			save: PRD1010_R01List.length === 0,
			print: PRD1010_R01List.length === 0,
			excel: PRD1010_R01List.length === 0
		}));
	}, [selectedPRD1010_R01List, deletedPRD1010_R01List, PRD1010_R01List]);

	const getParams = (filter: PRD1010_R01Filter) => {
		const params: Partial<PRD1010_R01Filter> = {
			compCd: filter.compCd || undefined,
			yearMonth: filter.yearMonth || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getPRD1010_R01AllCount(getParams(PRD1010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [PRD1010_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (messageMode === 'show') {
			setScrollTop(null);
		}
	
		let initialListLength = PRD1010_R01List.length;
		// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setPRD1010_R01List([]);
		}

		const responseData = await getPRD1010_R01List(
			'',
			getParams(PRD1010_R01Filter),
			fetchType === 'refetch' ? initialListLength : PRD1010_R01List.length ?? 0
		);
	
		let newList = [...responseData.data.data ] as PRD1010_R01Dto[];
		setPRD1010_R01List(newList);

		const processAllCount = await renewalAllCount();
		setOriginalPRD1010_R01List(newList);
		setSelectedPRD1010_R01List([]);
		removeDeletedPRD1010_R01List(deletedPRD1010_R01List);
		if (messageMode === 'show') {
			gridRef.current?.api.ensureIndexVisible(0, 'top');
		}
	
		if (newList.length === 0) {
			// insertPRD1010_R01List([{ ...defaultPRD1010_R01Row }], 0);
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		} else {
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
				setTimeout(() => setMessage(""), 5000);
			}
		}
	
		setPRD1010_R01PastFilter(PRD1010_R01Filter);
		setLoadingSpinner(false);
	}, [
		PRD1010_R01Filter,  PRD1010_R01PastFilter, PRD1010_R01List, deletedPRD1010_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalPRD1010_R01List, setSelectedPRD1010_R01List, insertPRD1010_R01List,
		setScrollTop,
	]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch])

	const defaultPRD1010_R01Row = useCallback(() => {
		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(PRD1010_R01_ROW_VALIDATE, {
				compCdList,
			}),
		);
		if (!commonResult.valid) {
			return { ...defaultPRD1010_R01Dto };
		}

		return {
			...defaultPRD1010_R01Dto,
			compCd: compCdList[0].compCd,
			yearMonth: formatDateYear4(new Date()) + formatDateMonth(new Date()),
			inserted: true,
		};
	}, [defaultPRD1010_R01Dto, compCdList, validateCommonCodeLists]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);
	
	const handleInsertClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		InsertRowHandler({
			event: e,
			dataList: PRD1010_R01List,
			rowData: {...defaultPRD1010_R01Row(), valueIndex: -1},
			insertRow: insertPRD1010_R01List,
			defaultRowData: defaultPRD1010_R01Row(),
			setClickable
		});
	}, [PRD1010_R01List, insertPRD1010_R01List, setClickable, defaultPRD1010_R01Row]);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const response = await downloadExcel_PRD1010_R01(
			'',
			getParams(PRD1010_R01Filter),
			PRD1010_R01List.length ?? 0
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '생산계획 정보등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [PRD1010_R01Filter, PRD1010_R01List])

	

	const handlePrintClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const blob = await getPdfBlob_PRD1010_R01(
				'',
				getParams(PRD1010_R01Filter),
				PRD1010_R01List.length ?? 0,
				company?.compCd || ''
			);

			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, "report1");
				if (ok) {
					showSuccess("PDF 출력을 완료하였습니다.");
				} else {
					const fileURL = URL.createObjectURL(blob);
					window.open(fileURL, "_blank");
					setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				}
			} else {
				const fileURL = URL.createObjectURL(blob);
				window.open(fileURL, "_blank");
				setTimeout(() => URL.revokeObjectURL(fileURL), 5000);
				showSuccess("PDF를 새 탭에서 열었습니다.");
			}
		} catch (error) {
			setMessage("PDF 출력 중 오류가 발생했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [PRD1010_R01Filter, PRD1010_R01List, company?.compCd, printPdfFromBlob, setLoadingSpinner, setMessage]);

	const validateInsertData = (data: PRD1010_R01Dto, currentRowIndex: number) => {

		if(!validateString(data.yearMonth)) {
			showWarning('생산연월을 입력해주세요.'); return false;
		}
		if(!validateString(data.compCd)) {
			showWarning('사업장을 선택해주세요.'); return false;
		}
		if(!validateString(data.itemCd)) {
			showWarning('품목코드를 입력해주세요.'); return false;
		}
		if(!itemCdMap.has(data.itemCd)) {
			showWarning('품목코드가 해당 MES의 품목정보에 존재하지 않습니다.\n품목코드: ' + data.itemCd); return false;
		}
		if(data.planQty <= 0) {
			showWarning('생산계획수량을 입력해주세요.'); return false;
		}
		if(data.orderQty < 0) {
			showWarning('작업지시수량을 입력해주세요.'); return false;
		}

		return true;
	}
	
	
	const handleSaveClick = useCallback(async () => {
		const deletedValueIndexSet = new Set(deletedPRD1010_R01List.map(Number));
		const getProductPlanKey = (row: PRD1010_R01Dto) =>
			`${row.yearMonth.replace(/-/g, '')}|${row.compCd}|${row.itemCd}`;
		const activeList = PRD1010_R01List.filter(row => !deletedValueIndexSet.has(row.valueIndex));
		const productPlanKeySet = new Set<string>();
		for (const row of activeList) {
			const productPlanKey = getProductPlanKey(row);
			if (productPlanKeySet.has(productPlanKey)) {
				showWarning('동일한 생산년월, 사업장, 품목코드는 중복 등록할 수 없습니다.');
				return;
			}
			productPlanKeySet.add(productPlanKey);
		}

		// 신규 사용자 처리
		const insertedList: PRD1010_R01Dto[] = PRD1010_R01List.filter(row => 
			!deletedValueIndexSet.has(row.valueIndex) &&
			row.inserted === true
		);

		// 수정된 사용자만 필터링
		const modifiedList = PRD1010_R01List.filter(row => 
			!deletedValueIndexSet.has(row.valueIndex) &&
			row.isModified && !row.inserted
		);

		
        // 수정 사용자 유효성 검증
		let valid = true;
		modifiedList.forEach((value) => {
			let index = PRD1010_R01List.findIndex(row => row.valueIndex === value.valueIndex);
			if(!validateInsertData(value, index)) valid = false;
		});
		if(!valid) { return; }
        // 신규 사용자 유효성 검증
		valid = true;
		insertedList.forEach((value) => {
			let index = PRD1010_R01List.findIndex(row => row.valueIndex === value.valueIndex);
			if(!validateInsertData(value, index)) valid = false;
		});
		if(!valid) { return; }

		const deleteStringList = deletedPRD1010_R01List.map(item => item.toString());
		const deleteTargetList = PRD1010_R01List.filter(item => deleteStringList.includes(item.valueIndex.toString()));

		const deleteResponse = deletedPRD1010_R01List.length > 0
			? await Promise.all(deleteTargetList.map(value => deletePRD1010_R01({
				yearMonth: value.yearMonth,
				compCd: value.compCd,
				itemCd: value.itemCd,
			})))
			: undefined;

		const [insertedResponse, modifiedResponse] = await Promise.all([
			insertedList.length > 0 ? postPRD1010_R01(insertedList) : Promise.resolve(),
			modifiedList.length > 0 ? patchPRD1010_R01(modifiedList) : Promise.resolve(),
		]);

		if(insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('생산계획정보 저장이 완료되었습니다.');
			setMessage('생산계획정보 저장이 완료되었습니다.');
		} else if(deleteResponse) {
			showSuccess('생산계획정보 삭제가 완료되었습니다.');
			setMessage('생산계획정보 삭제가 완료되었습니다.');
		}


		setTimeout(() => { setMessage(''); }, 5000);
		await executeDataFetch('refetch', { messageMode: 'none' });

	}, [PRD1010_R01List, deletedPRD1010_R01List, originalPRD1010_R01List, executeDataFetch]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedPRD1010_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedPRD1010_R01List(deletedPRD1010_R01List);
			removeSelectedPRD1010_R01List();
			setPRD1010_R01List([...PRD1010_R01List]);
		} else if (selectedPRD1010_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedPRD1010_R01List(selectedPRD1010_R01List);
			removeSelectedPRD1010_R01List(selectedPRD1010_R01List);
		}
	}, [selectedPRD1010_R01List, deletedPRD1010_R01List, addDeletedPRD1010_R01List, removeDeletedPRD1010_R01List, removeSelectedPRD1010_R01List, setPRD1010_R01List, PRD1010_R01List]);

	return{
		clickable, setClickable,
		fetchData,
		defaultPRD1010_R01Row,
		initContainerData,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
		handleExcelClick,
		handlePrintClick,
		buttonDisabled,
	}
}
